import { useEffect, useState } from 'react';
import { Save, Trash2, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface Flag {
  key: string;
  value: unknown;
  description: string | null;
  updated_by: string | null;
  updated_at: string;
}

export function FeatureFlagsPage() {
  const [rows, setRows] = useState<Flag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('true');
  const [newDescription, setNewDescription] = useState('');

  function load() {
    api<{ data: Flag[] }>('/admin/feature-flags')
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }
  useEffect(load, []);

  async function upsert(key: string, raw: string, description: string | null) {
    setError(null);
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch {
      setError('Value must be valid JSON.');
      return;
    }
    try {
      await api(`/admin/feature-flags/${encodeURIComponent(key)}`, {
        method: 'PUT',
        json: { value, description },
      });
      load();
    } catch {
      setError('Save failed — check the key format.');
    }
  }

  async function remove(key: string) {
    if (!window.confirm(`Delete flag ${key}?`)) return;
    try {
      await api(`/admin/feature-flags/${encodeURIComponent(key)}`, { method: 'DELETE' });
      load();
    } catch {
      setError('Delete failed.');
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Feature flags</h1>
        <p className="text-sm text-muted-foreground">
          JSON-valued, platform-wide. For per-user gating, prefer a dedicated provider.
        </p>
      </header>
      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{rows?.length ?? 0} flags</CardTitle>
        </CardHeader>
        <CardContent>
          {rows === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {rows?.length === 0 && <p className="text-sm text-muted-foreground">No flags set.</p>}
          {rows !== null && rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((f) => (
                <FlagRow
                  key={f.key}
                  flag={f}
                  onSave={(raw, desc) => void upsert(f.key, raw, desc)}
                  onDelete={() => void remove(f.key)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add a flag</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              void upsert(newKey, newValue, newDescription.length > 0 ? newDescription : null);
              setNewKey('');
              setNewValue('true');
              setNewDescription('');
            }}
          >
            <div className="space-y-1.5">
              <Label>Key</Label>
              <Input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="e.g. linkedin_import.enabled"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Value (JSON)</Label>
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder='true / {"on": true, "percent": 25}'
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
            </div>
            <div className="lg:col-span-3">
              <Button type="submit" disabled={newKey === ''}>
                <Plus className="mr-2 h-4 w-4" /> Save flag
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FlagRow({
  flag,
  onSave,
  onDelete,
}: {
  flag: Flag;
  onSave: (raw: string, description: string | null) => void;
  onDelete: () => void;
}) {
  const [raw, setRaw] = useState(() => JSON.stringify(flag.value));
  const [description, setDescription] = useState(flag.description ?? '');
  const dirty = raw !== JSON.stringify(flag.value) || description !== (flag.description ?? '');

  return (
    <li className="rounded-2xl border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <code className="font-mono text-sm">{flag.key}</code>
        <span className="ml-auto text-xs text-muted-foreground">
          updated {formatDateTime(flag.updated_at)}
        </span>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_2fr]">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
        />
        <Input
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          className="font-mono text-xs"
          placeholder="JSON value"
        />
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button
          size="sm"
          disabled={!dirty}
          onClick={() => onSave(raw, description.length > 0 ? description : null)}
        >
          <Save className="mr-1 h-4 w-4" /> Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="mr-1 h-4 w-4" /> Delete
        </Button>
      </div>
    </li>
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}
